import React from 'react';
import styled, { css } from 'styled-components';
import MetaTags from 'components/meta-tags';
import { CommonFooterLayout } from 'src/payments/layout/common/footer';
import { CommonLayoutHeader } from 'src/payments/layout/common/header';
import { ChildrenContainer } from 'src/payments/layout/common/children-container';
import { ImageOrientation, useGetImageOrientation } from 'hooks/use-get-image-orientation';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useObserver } from 'mobx-react-lite';
import { EnrollmentSteps } from 'src/payments/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DesktopOnly, MobileOnly } from 'shared/components';

import { Benefits } from './benefits';
import { Faq } from './faq';
import { HowItWorks } from './how-it-works';
import { SignupLogoMarquee } from './logo-marquee';
import { SignupEducationCarousel } from './education-carousel';

const { alreadySignedUp, success, connectBank } = EnrollmentSteps;

type DutchiePayLayoutProps = {
  children: React.ReactNode;
};

export function SignupLayout(props: DutchiePayLayoutProps): JSX.Element | null {
  const { children } = props;
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const flags = useFlags();
  const signupRedesignEnabled = flags[`fintech.platform.pbb-signup-redesign.rollout`] ?? false;
  const dispensaryLogo = useObserver(() => DutchiePayEnrollment.dispensary.logo ?? '');
  const dispensaryName = useObserver(() => DutchiePayEnrollment.dispensary.name ?? `Company`);
  const currentStep = useObserver(() => DutchiePayEnrollment.currentStep);
  const isCobrandedSignup = useObserver(() => DutchiePayEnrollment.isCobrandedSignup);
  const plaidModal = currentStep === connectBank;
  const currentStepEqualsSuccess = currentStep === success || currentStep === alreadySignedUp;
  const { imageOrientation } = useGetImageOrientation(dispensaryLogo);

  const content = (
    <ContentContainer $redesign={signupRedesignEnabled} data-testid='pbb-signup-content-container'>
      {signupRedesignEnabled ? (
        <LogoContainer $redesign data-testid='pbb-signup-logo-container'>
          <SignupLogoMarquee
            dispensaryLogo={isCobrandedSignup ? dispensaryLogo || null : null}
            dispensaryName={String(dispensaryName)}
          />
        </LogoContainer>
      ) : (
        dispensaryLogo &&
        isCobrandedSignup && (
          <LogoContainer $redesign={false} data-testid='pbb-signup-logo-container'>
            <DispensaryLogo
              src={dispensaryLogo}
              orientation={imageOrientation}
              alt={`${String(dispensaryName)} Logo`}
            />
          </LogoContainer>
        )
      )}
      <ChildrenContainer cardView={!currentStepEqualsSuccess && !plaidModal}>{children}</ChildrenContainer>
      {signupRedesignEnabled && (
        <MobileOnly display='block'>
          <MobileEducationStack>
            <HowItWorks />
            <Benefits />
            <Faq />
          </MobileEducationStack>
        </MobileOnly>
      )}
      {!currentStepEqualsSuccess && <SignupFooter $redesign={signupRedesignEnabled} />}
    </ContentContainer>
  );

  return (
    <>
      <MetaTags />
      <Container cardView={!currentStepEqualsSuccess && !plaidModal} $redesign={signupRedesignEnabled}>
        <CommonLayoutHeader />
        {signupRedesignEnabled ? (
          <MainLayout>
            <LeftPane>{content}</LeftPane>
            <DesktopOnly display='block'>
              <RightPane data-testid='pbb-signup-desktop-education-pane'>
                <SignupEducationCarousel />
              </RightPane>
            </DesktopOnly>
          </MainLayout>
        ) : (
          content
        )}
      </Container>
    </>
  );
}

const Container = styled.div<{ cardView: boolean; $redesign: boolean }>`
  /* Setting css vars on a parent scope makes them available to all child dom nodes */
  --dp-content-width: 375px;
  height: ${(props) => (props.cardView ? `100%` : '100vh')};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 24px;

  ${({ $redesign, theme }) =>
    $redesign &&
    css`
      ${theme.breakpoints.up('md')} {
        --dp-header-max-width: 100%;
        padding-bottom: 0;
        height: 100vh;
      }
    `}
`;

const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex: 1;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: 1fr;
    align-items: stretch;
  }
`;

const LeftPane = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    justify-content: center;
  }
`;

const MobileEducationStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const RightPane = styled.aside`
  background-color: var(--dcm-pbb-desktop-panel-background, #f0f9f6);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
`;

const ContentContainer = styled.div<{ $redesign: boolean }>`
  max-width: var(--dp-content-width);
  padding: 8px;
  width: 100%;

  ${({ $redesign }) =>
    $redesign &&
    css`
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 24px 8px;
    `}
`;

const SignupFooter = styled(CommonFooterLayout)<{ $redesign: boolean }>`
  ${({ $redesign }) =>
    $redesign &&
    css`
      margin-top: 0;
    `}
`;

const LogoContainer = styled.div<{ $redesign: boolean }>`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ $redesign }) => ($redesign ? '0' : '16px')};
`;

const DispensaryLogo = styled.img`
  max-height: ${(props) => (props.orientation === ImageOrientation.vertical ? '32px' : '56px')};
`;
